import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Plus, Trash2, RotateCcw, Undo2, Redo2, ListFilter } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import Chip from "../../../Shared/ui/Chip";
import Button from "../../../Shared/Buttons/Button";
import IconButton from "../../../Shared/Buttons/IconButton";
import { eventTypes } from "../../../../Config/constants";
import { fmt } from "../../../../Utils/formatCurrency";

// ─── recipe helpers (shared with ProjectionsTab) ─────────────────────────────

// The bundle's saved-on-server recipe as a list of composition items.
export function baseCompositionRecipe(bundle) {
  return (bundle.items || []).map((i) => ({
    priceDefinitionId: i.priceDefinitionId,
    multiplier: i.multiplier,
    isFixed: !!i.isFixed,
  }));
}

// Resolve a bundle's effective recipe: unsaved edits over the scenario's saved recipe over the base.
export function resolveCompositionRecipe(bundle, editComposition, savedItems) {
  if (editComposition && Object.prototype.hasOwnProperty.call(editComposition, bundle.id)) {
    return editComposition[bundle.id];
  }
  const saved = (savedItems || []).find((i) => i.itemId === bundle.id);
  if (saved && Array.isArray(saved.compositionItems) && saved.compositionItems.length) {
    return saved.compositionItems;
  }
  return baseCompositionRecipe(bundle);
}

const recipeKey = (r) => `${r.priceDefinitionId}:${Number(r.multiplier)}:${r.isFixed ? 1 : 0}`;

// Does an effective recipe differ from the bundle's base recipe? (order-independent)
export function recipeDiffersFromBase(recipe, bundle) {
  const base = baseCompositionRecipe(bundle);
  if (recipe.length !== base.length) return true;
  const baseSet = new Set(base.map(recipeKey));
  return recipe.some((r) => !baseSet.has(recipeKey(r)));
}

// Diff an effective recipe against the bundle's base recipe. Rows keep the base order (with removed
// definitions left in place so the user sees what disappeared), followed by added definitions.
// status: "unchanged" | "changed" | "added" | "removed".
export function diffCompositionRecipe(recipe, bundle) {
  const base = baseCompositionRecipe(bundle);
  const recipeByPd = new Map(recipe.map((r) => [r.priceDefinitionId, r]));
  const baseIds = new Set(base.map((b) => b.priceDefinitionId));
  const counts = { added: 0, removed: 0, changed: 0 };

  const rows = base.map((b) => {
    const r = recipeByPd.get(b.priceDefinitionId);
    if (!r) {
      counts.removed++;
      return { ...b, status: "removed", baseMultiplier: b.multiplier, baseIsFixed: b.isFixed };
    }
    const changed = Number(r.multiplier) !== Number(b.multiplier) || !!r.isFixed !== b.isFixed;
    if (changed) counts.changed++;
    return { ...r, isFixed: !!r.isFixed, status: changed ? "changed" : "unchanged", baseMultiplier: b.multiplier, baseIsFixed: b.isFixed };
  });
  recipe.forEach((r) => {
    if (baseIds.has(r.priceDefinitionId)) return;
    counts.added++;
    rows.push({ ...r, isFixed: !!r.isFixed, status: "added", baseMultiplier: null, baseIsFixed: null });
  });
  return { rows, counts };
}

// ─── UI ──────────────────────────────────────────────────────────────────────

const monoLabel = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: "var(--color-text-secondary)",
  fontFamily: "'DM Mono', monospace",
  marginBottom: 6,
};

const mono = { fontFamily: "'DM Mono', 'Courier New', monospace" };

// Visual treatment per diff status: accent bar + faint tint + chip.
const statusStyle = {
  added: { accent: "var(--color-success)", tint: "color-mix(in srgb, var(--color-success) 8%, transparent)", chip: "success", labelKey: "projectionsTab.diffNew" },
  changed: { accent: "var(--color-text-warning)", tint: "color-mix(in srgb, var(--color-text-warning) 7%, transparent)", chip: "warning", labelKey: "projectionsTab.diffChanged" },
  removed: { accent: "var(--color-danger)", tint: "color-mix(in srgb, var(--color-danger) 6%, transparent)", chip: "danger", labelKey: "projectionsTab.diffRemoved" },
};

const formatMultiplier = (m) => String(Number(m));

// A multiplier input with its own display state so typing "1," / "1.5" is smooth.
function MultiplierInput({ value, disabled, onCommit }) {
  const [display, setDisplay] = useState(() => String(value ?? 1));
  const lastRef = useRef(value);
  useLayoutEffect(() => {
    if (Number(value) !== Number(lastRef.current)) {
      lastRef.current = value;
      setDisplay(String(value ?? 1));
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(",", ".");
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
    setDisplay(raw);
    if (raw !== "" && !isNaN(parseFloat(raw))) {
      const num = Math.max(0.0001, Math.min(9999999999.9999, parseFloat(raw)));
      lastRef.current = num;
      onCommit(num);
    }
  };
  const handleBlur = () => {
    if (display === "" || isNaN(parseFloat(display))) {
      setDisplay("1");
      lastRef.current = 1;
      onCommit(1);
    }
  };

  return (
    <input
      type="text"
      value={display}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      data-composition-multiplier
      style={{
        width: 84,
        textAlign: "right",
        border: "1px solid var(--color-border-light)",
        borderRadius: 4,
        padding: "4px 8px",
        fontSize: 14,
        ...mono,
        outline: "none",
      }}
    />
  );
}

// One recipe row: definition name/code, diff status, current price, multiplier (with the original
// value when changed) and the row's actions (revert / restore / remove).
function RecipeRow({ item, currency, readOnly, onMultiplier, onRemove, onRevert, t }) {
  const st = statusStyle[item.status];
  const isRemoved = item.status === "removed";
  const isChanged = item.status === "changed";

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr minmax(90px, auto) 150px 68px", gap: 12,
      alignItems: "center", padding: "8px 8px 8px 10px", borderBottom: "1px solid var(--color-border-light)",
      borderLeft: `3px solid ${st ? st.accent : "transparent"}`,
      background: st ? st.tint : "transparent",
    }}>
      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          textDecoration: isRemoved ? "line-through" : "none",
          color: isRemoved ? "var(--color-text-muted)" : "inherit",
        }}>
          {item.code && <span style={{ ...mono, opacity: 0.7, marginRight: 6 }}>{item.code}</span>}
          {item.name}
        </span>
        {st && <Chip variant={st.chip} size="extraSmall">{t(st.labelKey)}</Chip>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", opacity: isRemoved ? 0.6 : 1 }}>
        <span style={{ fontSize: 12, ...mono, color: "var(--color-text-secondary)" }}>
          {item.price != null ? fmt(item.price, currency, 2) : "-"}
        </span>
        {item.uom && <Chip variant="default" size="extraSmall">{item.uom}</Chip>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
        {isChanged && (
          <span
            title={t('projectionsTab.diffOriginalMultiplier', { value: formatMultiplier(item.baseMultiplier) })}
            style={{ fontSize: 12, ...mono, color: "var(--color-text-muted)", textDecoration: "line-through", whiteSpace: "nowrap" }}
          >
            ×{formatMultiplier(item.baseMultiplier)}
          </span>
        )}
        {isRemoved ? (
          <span style={{ fontSize: 14, ...mono, color: "var(--color-text-muted)", textDecoration: "line-through", paddingRight: 9 }}>
            {formatMultiplier(item.multiplier)}
          </span>
        ) : (
          <MultiplierInput value={item.multiplier} disabled={readOnly} onCommit={(v) => onMultiplier(v)} />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {!readOnly && (isChanged || isRemoved) && (
          <IconButton
            ariaLabel={isRemoved ? t('projectionsTab.diffRestore') : t('projectionsTab.diffRevertMultiplier')}
            title={isRemoved ? t('projectionsTab.diffRestore') : t('projectionsTab.diffRevertMultiplier')}
            variant="ghost"
            size="small"
            onClick={onRevert}
          >
            <RotateCcw size={14} />
          </IconButton>
        )}
        {!readOnly && !isRemoved && (
          <IconButton
            ariaLabel={item.status === "added" ? t('projectionsTab.diffUndoAdd') : t('common.remove')}
            title={item.status === "added" ? t('projectionsTab.diffUndoAdd') : t('common.remove')}
            variant="ghost"
            size="small"
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </IconButton>
        )}
      </div>
    </div>
  );
}

// A scaled / fixed group inside a bundle card: rows + an "add definition" picker.
function RecipeGroup({ label, rows, addOptions, currency, readOnly, onMultiplier, onRemove, onRevert, onAdd, t }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={monoLabel}>{label}</div>
      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "4px 0 8px" }}>
          {t('projectionsTab.noDefinitionsInGroup')}
        </div>
      )}
      {rows.map((item) => (
        <RecipeRow
          key={`${item.priceDefinitionId}:${item.status === "removed" ? "r" : "p"}`}
          item={item}
          currency={currency}
          readOnly={readOnly}
          onMultiplier={(v) => onMultiplier(item.priceDefinitionId, v)}
          onRemove={() => onRemove(item.priceDefinitionId)}
          onRevert={() => onRevert(item)}
          t={t}
        />
      ))}
      {!readOnly && (
        <div style={{ marginTop: 8, maxWidth: 320 }}>
          <Select
            value={null}
            onChange={(opt) => opt && onAdd(opt.id)}
            searchable
            fullwidth
            size="small"
            placeholder={t('projectionsTab.addPriceDefinition')}
          >
            <SelectTrigger><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={13} />{t('projectionsTab.addPriceDefinition')}</span></SelectTrigger>
            <SelectContent>
              {addOptions.map((o) => (
                <SelectItem key={o.id} value={o} searchText={`${o.code ?? ""} ${o.name}`}>
                  <span style={{ display: "flex", justifyContent: "space-between", gap: 10, width: "100%" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.code ? `${o.code} - ${o.name}` : o.name}
                    </span>
                    <span style={{ ...mono, opacity: 0.7, whiteSpace: "nowrap" }}>
                      {o.currentValue != null ? fmt(o.currentValue, currency, 2) : t('projectionsTab.noPrice')}{o.uom ? `/${o.uom}` : ""}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// Compact "+1 added · 1 removed · 2 changed" chips for a diff count summary.
function DiffCountChips({ counts, t }) {
  return (
    <>
      {counts.added > 0 && <Chip variant="success" size="extraSmall">{t('projectionsTab.diffAddedCount', { count: counts.added })}</Chip>}
      {counts.removed > 0 && <Chip variant="danger" size="extraSmall">{t('projectionsTab.diffRemovedCount', { count: counts.removed })}</Chip>}
      {counts.changed > 0 && <Chip variant="warning" size="extraSmall">{t('projectionsTab.diffChangedCount', { count: counts.changed })}</Chip>}
    </>
  );
}

function BundleCard({ bundle, recipe, diff, isModified, optById, currency, readOnly, onChange, onReset, t }) {
  const [open, setOpen] = useState(isModified);

  const isTime = bundle.costBundleTypeId === eventTypes.TIME_BASED;
  const baseByPd = new Map((bundle.items || []).map((i) => [i.priceDefinitionId, i]));

  const resolveItem = (r) => {
    const baseItem = baseByPd.get(r.priceDefinitionId);
    const opt = optById.get(r.priceDefinitionId);
    return {
      ...r,
      name: baseItem?.priceDefinitionName ?? opt?.name ?? `#${r.priceDefinitionId}`,
      code: opt?.code ?? null,
      price: baseItem ? baseItem.price : (opt?.currentValue ?? null),
      uom: baseItem?.unitOfMeasurement ?? opt?.uom ?? null,
    };
  };

  const resolved = diff.rows.map(resolveItem);
  const scaledRows = resolved.filter((r) => !r.isFixed);
  const fixedRows = resolved.filter((r) => r.isFixed);

  const priceOf = (pdId) => {
    const baseItem = baseByPd.get(pdId);
    if (baseItem) return baseItem.price ?? 0;
    return optById.get(pdId)?.currentValue ?? 0;
  };
  const baseTotal = (bundle.items || []).reduce((s, i) => s + (i.price ?? 0) * i.multiplier, 0);
  const newTotal = recipe.reduce((s, r) => s + priceOf(r.priceDefinitionId) * Number(r.multiplier), 0);

  // Mutations produce a full new recipe and bubble up. The coalesce key groups consecutive keystrokes
  // in the same multiplier field into a single undo step.
  const emit = (next, coalesceKey) => onChange(bundle.id, next, coalesceKey);
  const setMultiplier = (pdId, v) => emit(recipe.map((r) => (r.priceDefinitionId === pdId ? { ...r, multiplier: v } : r)), `mult:${pdId}`);
  const removeItem = (pdId) => emit(recipe.filter((r) => r.priceDefinitionId !== pdId));
  const addItem = (pdId, isFixed) => {
    if (recipe.some((r) => r.priceDefinitionId === pdId)) return;
    emit([...recipe, { priceDefinitionId: pdId, multiplier: 1, isFixed }]);
  };
  // Revert a changed row to its original multiplier/group, or put a removed row back in its original
  // position (relative to the other base definitions still in the recipe).
  const revertItem = (row) => {
    const original = { priceDefinitionId: row.priceDefinitionId, multiplier: row.baseMultiplier, isFixed: row.baseIsFixed };
    if (row.status === "changed") {
      emit(recipe.map((r) => (r.priceDefinitionId === row.priceDefinitionId ? original : r)));
      return;
    }
    const baseOrder = (bundle.items || []).map((i) => i.priceDefinitionId);
    const baseIdx = baseOrder.indexOf(row.priceDefinitionId);
    const insertAt = recipe.findIndex((r) => {
      const idx = baseOrder.indexOf(r.priceDefinitionId);
      return idx === -1 || idx > baseIdx;
    });
    const next = [...recipe];
    next.splice(insertAt === -1 ? next.length : insertAt, 0, original);
    emit(next);
  };

  const presentIds = new Set(recipe.map((r) => r.priceDefinitionId));
  const addOptions = (optById.size ? Array.from(optById.values()) : []).filter((o) => !presentIds.has(o.id));

  const typeLabel = isTime
    ? t('projectionsTab.bundleTypeTime')
    : bundle.costBundleTypeId === eventTypes.RECURRING
    ? t('projectionsTab.bundleTypeRecurring')
    : t('projectionsTab.bundleTypeEvent');

  const groupProps = { addOptions, currency, readOnly, onMultiplier: setMultiplier, onRemove: removeItem, onRevert: revertItem, t };

  return (
    <div style={{ border: `1px solid ${isModified ? "var(--color-border-simulation)" : "var(--color-border-light)"}`, borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer", background: isModified ? "var(--color-primary-tint)" : "#fff" }}
      >
        <ChevronRight size={16} style={{ transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "none", flexShrink: 0, color: "var(--color-text-secondary)" }} />
        <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{bundle.name}</span>
        <Chip variant="default" size="extraSmall">{typeLabel}</Chip>
        {isModified && <DiffCountChips counts={diff.counts} t={t} />}
        <span style={{ fontSize: 12, ...mono, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
          {isModified ? `${fmt(baseTotal, currency, 2)} → ${fmt(newTotal, currency, 2)}` : fmt(baseTotal, currency, 2)}
        </span>
        {isModified && !readOnly && (
          <IconButton
            ariaLabel={t('projectionsTab.resetBundle')}
            title={t('projectionsTab.resetBundle')}
            variant="ghost"
            size="small"
            onClick={(e) => { e.stopPropagation(); onReset(bundle.id); }}
          >
            <RotateCcw size={14} />
          </IconButton>
        )}
      </div>
      {open && (
        <div style={{ padding: "4px 12px 12px" }}>
          <RecipeGroup
            label={isTime ? t('projectionsTab.hourlyDefinitions') : t('projectionsTab.priceDefinitions')}
            rows={scaledRows}
            onAdd={(pdId) => addItem(pdId, false)}
            {...groupProps}
          />
          {isTime && (
            <RecipeGroup
              label={t('projectionsTab.fixedCosts')}
              rows={fixedRows}
              onAdd={(pdId) => addItem(pdId, true)}
              {...groupProps}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Summary of all changes in the scenario plus undo/redo, a "modified only" filter and revert-all.
function ChangesToolbar({ modifiedCount, bundleCount, totals, modifiedOnly, onToggleModifiedOnly, canUndo, canRedo, onUndo, onRedo, onResetAll, readOnly, t }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "8px 10px", marginBottom: 10, borderRadius: 8,
      border: "1px solid var(--color-border-light)", background: "var(--color-primary-subtle)",
    }}>
      <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
        {modifiedCount === 0
          ? t('projectionsTab.diffNoChanges')
          : t('projectionsTab.diffBundlesModified', { count: modifiedCount, total: bundleCount })}
      </span>
      <DiffCountChips counts={totals} t={t} />
      <div style={{ flex: 1 }} />
      <Button
        variant={modifiedOnly ? "primary" : "light"}
        size="small"
        startIcon={<ListFilter size={14} />}
        onClick={onToggleModifiedOnly}
      >
        {t('projectionsTab.diffModifiedOnly')}
      </Button>
      {!readOnly && (
        <>
          <IconButton ariaLabel={t('projectionsTab.undo')} title={`${t('projectionsTab.undo')} (Ctrl+Z)`} variant="ghost" size="small" disabled={!canUndo} onClick={onUndo}>
            <Undo2 size={15} />
          </IconButton>
          <IconButton ariaLabel={t('projectionsTab.redo')} title={`${t('projectionsTab.redo')} (Ctrl+Shift+Z)`} variant="ghost" size="small" disabled={!canRedo} onClick={onRedo}>
            <Redo2 size={15} />
          </IconButton>
          <Button variant="light" size="small" startIcon={<RotateCcw size={14} />} disabled={modifiedCount === 0} onClick={onResetAll}>
            {t('projectionsTab.diffRevertAll')}
          </Button>
        </>
      )}
    </div>
  );
}

// Editor body for a change-composition scenario: every price bundle used by the graph, grouped by
// company, each expandable to edit its recipe (multipliers, add/remove definitions). Every change is
// shown as a diff against the bundle's original recipe and can be undone per row, per bundle, in one
// go, or step by step via undo/redo. Composition is view-only inside the tab, so there is no cross-tab apply.
export default function CompositionEditorBody({
  bundles, activeScenario, editComposition, onChange, onReset, onResetAll,
  onUndo, onRedo, canUndo, canRedo, substituteOptions, readOnly, currency,
}) {
  const { t } = useTranslation();
  const [modifiedOnly, setModifiedOnly] = useState(false);

  const optById = React.useMemo(() => {
    const map = new Map();
    (substituteOptions || []).forEach((o) => map.set(o.id, o));
    return map;
  }, [substituteOptions]);

  // Keyboard undo/redo. Other text fields (e.g. select search) keep their native behaviour; the
  // multiplier inputs are part of the recipe so they use the recipe history.
  useEffect(() => {
    if (readOnly) return undefined;
    const handler = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key !== "z" && key !== "y") return;
      const el = e.target;
      const isTextField = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (isTextField && !el.hasAttribute("data-composition-multiplier")) return;
      const redo = key === "y" || e.shiftKey;
      if (redo ? !canRedo : !canUndo) return;
      e.preventDefault();
      if (redo) onRedo(); else onUndo();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [readOnly, canUndo, canRedo, onUndo, onRedo]);

  if (!bundles || bundles.length === 0) {
    return <div style={{ padding: "16px", fontSize: 13, color: "var(--color-text-secondary)" }}>{t('projectionsTab.noComposableBundles')}</div>;
  }

  // Resolve every bundle's recipe + diff once.
  const entries = bundles.map((bundle) => {
    const recipe = resolveCompositionRecipe(bundle, editComposition, activeScenario.items);
    const isModified = recipeDiffersFromBase(recipe, bundle);
    return { bundle, recipe, isModified, diff: diffCompositionRecipe(recipe, bundle) };
  });
  const modified = entries.filter((e) => e.isModified);
  const totals = modified.reduce(
    (acc, e) => ({ added: acc.added + e.diff.counts.added, removed: acc.removed + e.diff.counts.removed, changed: acc.changed + e.diff.counts.changed }),
    { added: 0, removed: 0, changed: 0 }
  );
  const visible = modifiedOnly ? modified : entries;

  // Group bundles by company for readability.
  const byCompany = new Map();
  visible.forEach((e) => {
    const key = e.bundle.companyName || "";
    if (!byCompany.has(key)) byCompany.set(key, []);
    byCompany.get(key).push(e);
  });
  const showCompanyHeaders = byCompany.size > 1;

  return (
    <div style={{ padding: "8px 16px 12px" }}>
      <ChangesToolbar
        modifiedCount={modified.length}
        bundleCount={entries.length}
        totals={totals}
        modifiedOnly={modifiedOnly}
        onToggleModifiedOnly={() => setModifiedOnly((v) => !v)}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={onUndo}
        onRedo={onRedo}
        onResetAll={onResetAll}
        readOnly={readOnly}
        t={t}
      />
      {visible.length === 0 && (
        <div style={{ padding: "8px 4px", fontSize: 13, color: "var(--color-text-secondary)" }}>{t('projectionsTab.diffNoModifiedBundles')}</div>
      )}
      {Array.from(byCompany.entries()).map(([companyName, companyEntries]) => (
        <div key={companyName} style={{ marginBottom: 8 }}>
          {showCompanyHeaders && companyName && <div style={monoLabel}>{companyName}</div>}
          {companyEntries.map(({ bundle, recipe, isModified, diff }) => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              recipe={recipe}
              diff={diff}
              isModified={isModified}
              optById={optById}
              currency={currency}
              readOnly={readOnly}
              onChange={onChange}
              onReset={onReset}
              t={t}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
